import { useReportTiktokCampaigns } from './useReportTiktokCampaigns';
import ReportCampaignsTable from '../CampaignsTable/ReportCampaignsTable';
import TiktokReportPostsTable from '../../Posts/PostsTable/TiktokReportPostsTable';
import { ExpandedState } from '@tanstack/react-table';
import useUrlState from 'Hooks/useUrlState';
import { useMemo } from 'react';
import { useReorderReportCampaigns } from '../hooks/useReorderReportCampaigns';
import TikTokAudioModal from 'Modules/Plans/Audios/AudioModal/TikTokAudioModal/TikTokAudioModal';

type UrlState = Partial<{
    expanded: string;
    audioId: number;
}>;

const ReportTiktokCampaigns = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>();
    const {
        rows,
        isLoading: areCampaignsLoading,
        isTeamsLoading,
        isProjectsLoading,
        isAudioTimeSeriesLoading,
        isAudioTimeSeriesError,
        getIsBrandLoading,
        getIsCampaignStatsLoading,
    } = useReportTiktokCampaigns();

    const expandedState = useMemo(() => {
        const expanded = urlState?.expanded;
        if (!expanded) {
            return {};
        }

        return expanded.split(',').reduce(
            (acc, campaignId) => {
                acc[campaignId] = true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [urlState]);

    const setExpandedState = (expanded: ExpandedState) => {
        const expandedKeys = Object.keys(expanded).filter((key) => expanded[key as keyof ExpandedState]);
        setUrlState({ expanded: expandedKeys.join(',') });
    };

    const reorderCampaigns = useReorderReportCampaigns('tiktok');

    return (
        <>
            <ReportCampaignsTable
                data={rows}
                isLoading={areCampaignsLoading}
                expandedState={expandedState}
                onExpandedChange={setExpandedState}
                renderSubComponent={(row) => <TiktokReportPostsTable campaign={row.original} />}
                meta={{
                    isLoading: areCampaignsLoading,
                    getIsCampaignStatsLoading: (row) => getIsCampaignStatsLoading(row.id),
                    getIsBrandLoading: (row) => getIsBrandLoading(row.id),
                    getIsProjectLoading: () => isProjectsLoading,
                    getIsTeamLoading: () => isTeamsLoading,
                    getIsTimeSeriesLoading: () => isAudioTimeSeriesLoading,
                    getTimeSeriesHasError: () => isAudioTimeSeriesError,
                    onAudioGrowthCellClick: (row) => {
                        if (row.tiktok_details?.audio_id) {
                            setUrlState({ audioId: row.tiktok_details.audio_id });
                            return;
                        }
                    },
                }}
                onDragEnd={reorderCampaigns}
            />

            <TikTokAudioModal
                isOpen={Boolean(urlState.audioId)}
                onClose={() => setUrlState({ audioId: undefined })}
                audioId={Number(urlState.audioId)}
            />
        </>
    );
};

export default ReportTiktokCampaigns;
